import { Grid, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { ThemePalette } from 'mui.theme';
import WarningIcon from '@mui/icons-material/Warning';
import type { totalFleetDetails } from 'types/production.types';
import { convertToTwoDecimalPlaces } from 'components/Charts/Tooltip';
import { daysLeftInYear } from 'views/Utilization/utils';
import CircularProgressChart from 'views/Production/components/CircularProgress/CircularProgress';
import MultiColorCircularLoader from 'views/Production/components/BoxLoader/MultiColorCircularLoader';
import { formatNumberToShortScale } from 'views/Production/Hauler/component/utils';
import { hasMetTarget, targetPercentageDiff } from '../utils';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useEffect } from 'react';
import { useAppSelector } from 'store/hook';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  enableDarkTheme: boolean;
  data: totalFleetDetails;
  isLoading: boolean;
  unitSystem: string;
  currentDailyAverage: number;
  rollingDailyAverage: number;
  isLoadingFleetPeriodSummary: boolean;
  setDetailsVal: any;
}

const FleetProductionDetails = ({
  enableDarkTheme,
  data,
  isLoading,
  unitSystem,
  currentDailyAverage,
  rollingDailyAverage,
  isLoadingFleetPeriodSummary,
  setDetailsVal,
}: Props) => {
  const { t } = useTranslation();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const target = useAppSelector((state) => state.targetReducer.data.production);
  const productionTarget = target?.find((item) => item.assetType === 'all');

  useEffect(() => {
    if (!isLoadingFleetPeriodSummary) {
      setDetailsVal({
        'YTD Target (Tons)': formatNumberToShortScale(
          convertToTwoDecimalPlaces(
            (productionTarget?.ytdTarget as number)?.toString()
          )
        ),
        'Annual Production Target (Tons)': formatNumberToShortScale(
          convertToTwoDecimalPlaces(
            (productionTarget?.targetValue as number)?.toString()
          )
        ),
        [`Rolling Daily Average 7 Days (Tons/Day)`]: formatNumberToShortScale(
          convertToTwoDecimalPlaces((rollingDailyAverage || 0).toString())
        ),
        'Remaining Days': daysLeftInYear(),
        [`Current Daily Average 7 Days (Tons/Day)`]: formatNumberToShortScale(
          convertToTwoDecimalPlaces((currentDailyAverage ?? 0).toString())
        ),
        // 'Target Met (True/False)': hasMetTarget(data.tons, target),
        'Target Met (True/False)': hasMetTarget(
          data.tons,
          productionTarget?.targetValue as number
        ),
      });
    }
  }, [
    isLoadingFleetPeriodSummary,
    data,
    rollingDailyAverage,
    currentDailyAverage,
    hasMetTarget,
  ]);
  return (
    <Grid item lg={6} md={6} sm={12}>
      <Box
        sx={{
          padding: 2,
          backgroundColor: enableDarkTheme
            ? palette.boxBackground
            : palette.toolBarBackground,
          borderRadius: '5px',
          border: `1px solid ${palette.cardBorderColor}`,
          minHeight: '300px',
          maxHeight: '300px',
          overflowY: 'auto',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Typography
              sx={{
                fontWeight: '600',
                fontSize: '13px',
                paddingTop: '5px',
                paddingBottom: '4.5px',
              }}
            >
              {t('total_fleet_production_in_tons_ytd')}
            </Typography>

            {/* <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              <Typography
                sx={{
                  fontSize: '12px',
                }}
              >
                Mine Production Plan:
              </Typography>
              <Button
                size="small"
                sx={{
                  border: '1px solid #4A7BFA',
                  color: palette.textPrimary,
                  textTransform: 'none',
                  paddingLeft: '20px',
                  paddingRight: '20px',
                  fontSize: '11px',
                }}
                startIcon={<FileUploadIcon />}
              >
                Upload
              </Button>
            </Box> */}
          </Box>
        </Box>

        {isLoading ? (
          <MultiColorCircularLoader height="300px" />
        ) : (
          <Box
            sx={{
              paddingTop: '15px',
            }}
          >
            <Grid container>
              <Grid item lg={6} sm={12}>
                <Box
                  sx={{
                    display: 'flex',
                  }}
                >
                  <CircularProgressChart
                    enableDarkTheme={enableDarkTheme}
                    value={data.tons}
                    target={
                      (productionTarget?.ytdTarget as number) || data.ytdTarget
                    }
                    unitSystem={unitSystem}
                  />
                  <Box
                    sx={{
                      alignContent: 'center',
                      paddingLeft: '10px',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: '11px',
                        color: palette.textSecondary,
                      }}
                    >
                      {t('ytd_target')}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '11px',
                        color: palette.textPrimary,
                        paddingBottom: '15px',
                      }}
                    >
                      {formatNumberToShortScale(
                        convertToTwoDecimalPlaces(
                          (productionTarget?.ytdTarget as number)?.toString()
                        )
                      )}{' '}
                      {t('tons_unit')}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '11px',
                        color: palette.textSecondary,
                      }}
                    >
                      {t('annual_target')}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: '11px',
                        color: palette.textPrimary,
                      }}
                    >
                      {formatNumberToShortScale(
                        convertToTwoDecimalPlaces(
                          (productionTarget?.targetValue as number)?.toString()
                        )
                      )}{' '}
                      {t('tons_unit')}
                    </Typography>
                  </Box>
                </Box>
              </Grid>

              <Grid item lg={6} sm={12}>
                <Box>
                  <Grid container>
                    <Grid item lg={6} sm={12}>
                      <Typography
                        sx={{
                          fontSize: '12px',
                          color: palette.textSecondary,
                        }}
                      >
                        {t('annual_production_target')}
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: '12px',
                          color: palette.textPrimary,
                          paddingTop: '10px',
                        }}
                      >
                        {formatNumberToShortScale(
                          convertToTwoDecimalPlaces(
                            (
                              productionTarget?.targetValue as number
                            )?.toString()
                          )
                        )}
                        <span
                          style={{
                            color: palette.textSecondary,
                          }}
                        >
                          {' '}
                          {t('tons_unit')}
                        </span>
                      </Typography>

                      <Typography
                        sx={{
                          paddingTop: '30px',
                          fontSize: '12px',
                          color: palette.textSecondary,
                        }}
                      >
                        {t('rolling_daily_average_7d')}
                      </Typography>
                      {isLoadingFleetPeriodSummary ? (
                        <Typography
                          sx={{
                            fontSize: '13px',
                            color: palette.textPrimary,
                            paddingTop: '10px',
                          }}
                        >
                          {t('cdp_loading')}
                        </Typography>
                      ) : (
                        <Typography
                          sx={{
                            fontSize: '13px',
                            color: palette.textPrimary,
                            paddingTop: '10px',
                          }}
                        >
                          {formatNumberToShortScale(
                            convertToTwoDecimalPlaces(
                              (rollingDailyAverage || 0).toString()
                            )
                          )}
                          <span
                            style={{
                              color: palette.textSecondary,
                            }}
                          >
                            {' '}
                            {'Tons/Day'}
                          </span>
                        </Typography>
                      )}
                    </Grid>
                    <Grid item lg={6} sm={12}>
                      <Typography
                        sx={{
                          fontSize: '12px',
                          color: palette.textSecondary,
                        }}
                      >
                        {t('remaining_days')}
                      </Typography>

                      <Typography
                        sx={{
                          fontSize: '13px',
                          color: palette.textPrimary,
                          paddingTop: '10px',
                        }}
                      >
                        {daysLeftInYear()}
                      </Typography>

                      <Typography
                        sx={{
                          paddingTop: '30px',
                          fontSize: '12px',
                          color: palette.textSecondary,
                        }}
                      >
                        {t('current_daily_average_7d')}
                      </Typography>
                      {isLoadingFleetPeriodSummary ? (
                        <Typography
                          sx={{
                            fontSize: '12px',
                            color: palette.textPrimary,
                            paddingTop: '10px',
                          }}
                        >
                          {t('cdp_loading')}
                        </Typography>
                      ) : (
                        <Typography
                          sx={{
                            fontSize: '13px',
                            color: palette.textPrimary,
                            paddingTop: '10px',
                          }}
                        >
                          {isLoadingFleetPeriodSummary
                            ? 'Loading...'
                            : formatNumberToShortScale(
                                convertToTwoDecimalPlaces(
                                  (currentDailyAverage ?? 0).toString()
                                )
                              )}
                          <span
                            style={{
                              color: palette.textSecondary,
                            }}
                          >
                            {' '}
                            {'Tons/Day'}
                          </span>
                        </Typography>
                      )}
                    </Grid>
                  </Grid>

                  {hasMetTarget(
                    data,
                    productionTarget?.targetValue as number
                  ) ? (
                    <Box
                      sx={{
                        textAlign: 'center',
                        border: `1px solid ${palette.cardBorderColor}`,
                        borderRadius: '5px',
                        marginTop: '15px',
                        padding: '10px',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '12px',
                          display: 'flex',
                          alignItems: 'center',
                        }}
                      >
                        <CheckCircleIcon
                          sx={{ color: '#029161', marginRight: '4px' }}
                        />
                        {t('avg_production_is_on_target')}
                      </Typography>
                    </Box>
                  ) : (
                    // <Box
                    //   sx={{
                    //     textAlign: 'center',
                    //     border: '1px solid #2F445F',
                    //     borderRadius: '5px',
                    //     marginTop: '15px',
                    //     padding: '10px',
                    //     display: 'flex',
                    //     justifyContent: 'center',
                    //     alignItems: 'center',
                    //   }}
                    // >
                    //   <Typography
                    //     sx={{
                    //       fontSize: '12px',
                    //       display: 'flex',
                    //       alignItems: 'center',
                    //     }}
                    //   >
                    //     <WarningIcon
                    //       sx={{ color: '#FFB402', marginRight: '4px' }}
                    //     />
                    //     Avg Production needs to go up by
                    //     <span
                    //       style={{
                    //         color: '#FFB402',
                    //         marginLeft: '4px',
                    //       }}
                    //     >
                    //       {targetPercentageDiff(data)}%
                    //     </span>
                    //   </Typography>
                    // </Box>
                    <></>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Box>
        )}

        <Box
          sx={{
            marginTop: '50px', // Pushes the box to the bottom
          }}
        >
          <Typography
            sx={{
              fontSize: '10px',
              textAlign: 'right',
              color: palette.textSecondary,
              marginBottom: '4px',
            }}
          >
            {t('averages_shown_above_are_based_on_last_7_days_recent_most_da')}
          </Typography>
          <Typography
            sx={{
              fontSize: '10px',
              textAlign: 'right',
              color: palette.textSecondary,
            }}
          >
            {t('ytd_numbers_are_based_on_real_time_data_received_from_assets')}
          </Typography>
        </Box>
      </Box>
    </Grid>
  );
};

export default FleetProductionDetails;
